import React, {
  Component,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import App from "../app";
import { invalid, removeAt, type } from "@codeArt/utils/common";
import { createKeepId } from "../root";
import KeepAlive, {
  useActivate,
  useUnactivate,
  withActivation
} from "react-activation";
import { Tracker } from "./track";
import Base64 from "base-64";
import { Scroller } from "../app";
import { makeStyles, withStyles } from "@material-ui/styles";
import { Box, ThemeProvider } from "@material-ui/core";
import clsx from "clsx";
import { getTheme } from "@jumbo/components/AppWrapper";
import { useLocation } from "react-router";
import AppContext from "@jumbo/components/contextProvider/AppContextProvider/AppContext";
// import KeepContainer from "./keepContainer";
import { bindBroadcast } from "./broadcast";
import PageLoader from "@jumbo/components/PageComponents/PageLoader";
import { Request } from "@codeArt/request";
import { useDispatch, useSelector } from "react-redux";
import { Scrollbar } from "@codeArt/components/Scrollbar";
import { memo } from "@codeArt/components/common";
import { HorizontalTabs } from "@codeArt/components/tab/hori";
import { VerticalTabs } from "@codeArt/components/tab/vert";
import { Auth, Principal } from "@codeArt/auth";
import MultiPage from "./multi";
import { setStatus, PageStatus } from "@codeArt/slices/page";
import Portal from "@codeArt/components/layout/Portal";
import { empty } from "../../utils/common";

let _ = require("lodash");

let pageIds = {};
function createPageId(key) {
  //根据页面路径，生成唯一的keepId
  let pageId = pageIds[key];
  if (!pageId) {
    pageId = _.uniqueId("pageId_");
    pageIds[key] = pageId;
  }
  return pageId;
}


export function setTitle(title, { project, headerLogo }) {
  const name = headerLogo ? headerLogo.name : project.name;
  document.title = title ? `${title} - ${name}` : name;
}

export function KeepContainer({
  children,
  onMounted,
  onUnmounted,
  onActivated,
  onUnactivated
}) {
  const mounted = () => {
    if (onMounted) onMounted();
  };

  const unmounted = () => {
    if (onUnmounted) onUnmounted();
  };

  const activated = () => {
    if (onActivated) onActivated();
  };

  const unactivated = () => {
    if (onUnactivated) onUnactivated();
  };

  useEffect(() => {
    mounted();
    return () => {
      unmounted();
    };
  }, []); //追加 []参数，表示只触发一次

  useActivate(() => {
    activated();
  });

  useUnactivate(() => {
    unactivated();
  });

  return children;
}

const useStyles = makeStyles(theme => ({
  appMainContent: props => ({
    flex: 1,
    display: "flex",
    height: "100%",
    flexDirection: "column",
    boxSizing: "border-box"
    // padding: "20px 15px",
    // [theme.breakpoints.up("md")]: {
    //   paddingLeft: 50,
    //   paddingRight: 50
    // },
    // [theme.breakpoints.up("lg")]: {
    //   paddingLeft: 65,
    //   paddingRight: 65
    // },
    // [theme.breakpoints.up("xl")]: {
    //   paddingLeft: 88,
    //   paddingRight: 88
    // },
    // [theme.breakpoints.down("sm")]: {
    //   paddingTop: 20,
    //   paddingBottom: 20
    // }
  }),
  pageHeader: ({ narrow,alone }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    backgroundColor: "#fff",
    height: 56,
    border:alone?"1px solid #e6e6e6":"",
    borderBottom: "1px solid #e6e6e6",
    marginLeft:alone?10:0,
    marginRight:alone?10:0,
    marginTop:alone?10:0,
    [theme.breakpoints.down("sm")]: {
      height: 56
    }
  }),
  pageHeaderCore: ({ narrow }) => {
    let style = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      height: "inherit",
      "& > h1": {
        fontSize: "0.98rem",
        fontWeight: "bold",
        color: "rgba(0, 0, 0, 0.87)"
      }
    };

    if (narrow) {
      style = {
        ...style,
        width: "1300px",
        [theme.breakpoints.down(1300)]: {
          width: '100%',
        },
      }
    }

    return style;
  },
  pageBody: props => ({
    display: "flex",
    flexDirection: "column",
    flex: 1,
    padding: props.disablePadding ? 0 : 10,
    ...props.style
  })
}));

const _PageContainer = React.forwardRef(
  (
    {
      keep = true,
      keepClear = false,
      children,
      onActivated,
      onUnactivated,
      //对于以下事件，要注意一点：Page本身是不被keep的，但是我们通过一种手段，模拟出，在Page的内容keep期间，只会触发一次onMounted和onUnmounted事件
      onMounted,
      onUnmounted,
      getLayout
    },
    ref
  ) => {
    const location = useLocation();

    let pageId = useMemo(() => createPageId(location.pathname), []);
    let keepId = useMemo(() => (keep ? pageId : null), [
      keep
    ]); //注意pathname是忽略了参数的，如果想要识别参数keep，那要用另外的路径属性


    //#region 布局

    const setLayout = (layout) => {
      layout = layout || (getLayout ? getLayout() : null);
      if (!layout) return;
      App.current().applyLayout("set", layout);
    }

    const resetLayout = () => {
      if (!getLayout) return;
      App.current().applyLayout("reset");
    }

    //#endregion

    useImperativeHandle(ref, () => ({
      setLayout, resetLayout
    }));


    //#region 加载和卸载

    const handleMounted = useCallback(() => {
      if (keepId) registerKeep();
      if (keepClear) clearKeep();
      if (onMounted) onMounted();
      handleActivated();  //当非keep模式下，手工执行，确保事件有效,当keep模式下，由于第一次加载组件，keep是不会触发激活事件的，所以还是要手工触发
    }, []);

    const handleUnmounted = useCallback(() => {
      if (!keep) handleUnactivated();  //当keep下handleUnactivated会自动触发，但是非keep模式下，需要我们手工执行
      if (onUnmounted) onUnmounted();
    }, []);

    const handleActivated = () => {
      setLayout();

      Scroller.load(keepId);
      if (onActivated) onActivated();
    };

    const handleUnactivated = useCallback(() => {
      resetLayout();
      Scroller.save(keepId);
      if (onUnactivated) onUnactivated();
    }, []);

    //#endregion

    //#region keep

    const tryKeep = useCallback(children => {
      const container = ( //不论是否加入到KeepAlive里，都要用KeepContainer包裹，这样可以达成事件的统一
        <KeepContainer
          onActivated={handleActivated}
          onUnactivated={handleUnactivated}
          onMounted={handleMounted}
          onUnmounted={handleUnmounted}
        >
          {children}
        </KeepContainer>
      );

      if (keepId) {
        return <KeepAlive name={keepId}>{container}</KeepAlive>;
      }
      return container;
    }, []);

    const registerKeep = useCallback(() => {
      App.current().registerKeep(keepId);
    }, []);

    const clearKeep = useCallback(() => {
      App.current().clearKeep(keepClear, keepId);
    }, []);

    //#endregion

    const classes = useStyles();

    return (
      <Box
        className={clsx(classes.appMainContent, "Cmt-appMainContent")}
        bgcolor="background.main"
      >
        {tryKeep(children)}
      </Box>
    );
  });

const PageContainer = React.memo(_PageContainer, (prevProps, nextProps) => {
  return memo(prevProps, nextProps, []);
});

export const PageContent = (content) => { //使用PageContent包裹Content，会减少额外的渲染次数
  return React.memo(content, (prevProps, nextProps) => {
    return memo(prevProps, nextProps, []);
  });
}

const Page = React.forwardRef(
  (
    {
      title,
      header,
      body,
      children,
      loadParas,
      on,
      onMounted,
      onUnmounted,
      onActivated,
      onUnactivated
    },
    ref
  ) => {
    title = empty(title) ? "" : title;
    const dispatch = useDispatch();
    const { project, headerLogo } = useContext(AppContext);

    const location = useLocation();
    let pageId = useMemo(() => createPageId(location.pathname), []);

    const status = useSelector(({ page }) => page.status[pageId]) || PageStatus.idle;

    const handleStatus = (v) => {
      dispatch(setStatus({ pageId, status: v }));
    }

    setTitle(title, { project, headerLogo });

    const renderHeader = () => {
      if (!header) return null;
      return <PageHeader {...header} />;
    };

    const renderBody = () => {
      return <PageBody {...body}>{children}</PageBody>;
    };

    //#region  自带的加载，使用它可以防止加载过程中页面卸载导致的bug

    const loadable = () => status === PageStatus.idle && _life; //当没有加载过，并且处于激活状态下，那么可以加载

    const load = () => {
      if (!loadParas) return;
      if (!loadable()) return;

      handleStatus(PageStatus.loading);

      let success = loadParas.success;
      delete loadParas.success;

      Request.mute(
        {
          success: r => {
            if (!loadable()) return;
            handleStatus(PageStatus.loaded);
            if (success) success(r);
          },
          ...loadParas
        },
        dispatch
      );
    };

    const reload = () => {
      handleStatus(PageStatus.idle);
    };

    useEffect(
      () => {
        load(); //在这里侦听loaded的改变，当为非true的时候，再次加载
      },
      [status]
    );

    //#endregion

    //#region  生命周期

    let _life = true;
    const mounted = () => {
      addEventable();
      let timeoutId = setTimeout(() => {
        //只有通过setTimeout后，执行的onMounted里才能即时性的更改state，更新UI，否则无效
        if (onMounted) onMounted();
        clearTimeout(timeoutId);
      }, 1);
    };

    const unmounted = () => {
      removeEventable();
      handleStatus(PageStatus.idle);
      if (onUnmounted) onUnmounted();
    };

    const activated = () => {
      load();
      if (onActivated) onActivated();
    };

    const unactivated = () => {
      _life = false;
      if (onUnactivated) onUnactivated();
    };

    //#endregion

    //#region 信息广播

    let _broadcastEvent = null;
    const addEventable = () => {
      _broadcastEvent = bindBroadcast(on);
    };

    const removeEventable = () => {
      if (_broadcastEvent) {
        _broadcastEvent.remove();
        _broadcastEvent = null;
      }
    };

    //#endregion

    useEffect(() => {
      mounted();
      return () => {
        unmounted();
      };
    }, []); //追加 []参数，表示只触发一次

    useActivate(() => {
      activated();
    });

    useUnactivate(() => {
      unactivated();
    });

    useImperativeHandle(ref, () => ({
      reload
    }));

    //为了防止reload时，整个主界面不见了，realod结束后，滚动条定位也没了，
    //所以，把PageLoad放在主界面上，而不是两者切换显示
    return (
      <React.Fragment>
        {status === PageStatus.loading ? <PageLoader /> : null}
        {renderHeader()}
        {renderBody()}
      </React.Fragment>
    );

    // return status === PageStatus.loading ? (
    //   <PageLoader />
    // ) : (
    //   <React.Fragment>
    //     {renderHeader()}
    //     {renderBody()}
    //   </React.Fragment>
    // );
  }
);

export const PageHeader = ({ title, right, narrow,alone }) => {
  title = !title ? "" : title;
  const classes = useStyles({ narrow,alone });
  return (
    <div className={classes.pageHeader}>
      {/* 为了水平居中显示，加两个core */}
      <div className={classes.pageHeaderCore}>
        {type(title) === "string" ? <h1>{title}</h1> : title}
        {right}
      </div>
    </div>
  );
};

const PageBody = ({ children, disablePadding, scrollable = true, style, className = "" }) => {
  //scrollable=false的设置适合整屏显示内容的场景
  const classes = useStyles({ style, disablePadding });

  const tryScroll = content => {
    return scrollable ? <Scrollbar>{content}</Scrollbar> : content;
  };

  return (
    <div className={clsx(classes.pageBody, className)}>
      {tryScroll(children)}
    </div>
  );
};

// const Page = React.memo(_Page, (prevProps, nextProps) => {
//   return memo(prevProps, nextProps, []);
// });


const PageEmpty = ({ title, children, flat }) => {

  let main = null;
  if (children) main = children;
  else if (title) main = <p className="c-text-s4 m-text-muted">{title}</p>

  return (
    <Portal full={true} className={"h-100"} flat={flat}>
      <div className={"d-flex flex-column justify-content-center align-items-center h-100"}>
        {main}
      </div>
    </Portal>
  );
}


export { Page, PageContainer, MultiPage, PageEmpty };


